<template>
    <v-container class="content-wide pt-4 pb-6">
        <div class="d-flex align-baseline flex-wrap ga-4 mb-4">
            <h1 class="home__greeting">{{ greeting }}</h1>
            <span v-if="digest" class="text-body-2 text-medium-emphasis">{{ digest }}</span>
        </div>

        <v-card class="mb-4">
            <v-card-title class="d-flex align-center">
                <span>Release radar</span>
                <v-spacer />
                <v-btn
                    variant="text"
                    size="small"
                    append-icon="mdi-arrow-right"
                    :to="{ name: 'ProjectList' }"
                >
                    All projects
                </v-btn>
            </v-card-title>
            <v-divider />
            <v-skeleton-loader v-if="loading" type="list-item-two-line@3" />
            <EmptyState
                v-else-if="radar.length === 0"
                icon="mdi-radar"
                title="Nothing on the radar"
                text="No active versions."
            />
            <v-list v-else class="striped py-0">
                <v-list-item
                    v-for="row in radar"
                    :key="row.versionId"
                    :to="{
                        name: 'ProjectView',
                        params: { projectId: row.projectId, tab: 'versions' },
                    }"
                    class="home-radar__row"
                >
                    <template #title>
                        <span class="font-weight-medium">{{ row.projectName }}</span>
                        <!-- Row is a router link; stop it so the chip opens the version's tickets instead. -->
                        <span
                            class="home-radar__version ml-2"
                            role="button"
                            tabindex="0"
                            :aria-label="`View tickets fixed in ${row.versionName}`"
                            @click.stop.prevent="viewVersionTickets(row.projectId, row.versionId)"
                            @keydown.enter.stop.prevent="
                                viewVersionTickets(row.projectId, row.versionId)
                            "
                            @keydown.space.stop.prevent="
                                viewVersionTickets(row.projectId, row.versionId)
                            "
                        >
                            <VersionChip :version="rowVersion(row)" />
                        </span>
                    </template>
                    <template #subtitle>
                        <span class="text-data">{{ scheduleFor(row) }}</span>
                        <span v-if="row.theme" class="home-radar__theme ml-2">
                            ·
                            <v-icon size="14" icon="mdi-flag-outline" class="mx-1" />
                            {{ row.theme }}
                        </span>
                    </template>
                    <template #append>
                        <v-chip
                            v-if="phaseFor(row)"
                            :color="phaseFor(row)!.color"
                            :variant="phaseFor(row)!.variant"
                            size="small"
                            class="mr-2"
                        >
                            <v-icon start size="14" icon="mdi-snowflake" />
                            {{ phaseFor(row)!.label }}
                        </v-chip>
                        <v-chip
                            size="small"
                            variant="tonal"
                            color="primary"
                            class="mr-4"
                            :aria-label="`View open tickets in ${row.versionName}`"
                            @click.stop.prevent="
                                viewVersionTickets(row.projectId, row.versionId, true)
                            "
                        >
                            {{ row.openTickets }} open
                        </v-chip>
                        <div class="home-radar__progress d-none d-sm-flex align-center">
                            <v-progress-linear
                                :model-value="progressFor(row)"
                                color="primary"
                                rounded
                                height="4"
                                class="mr-2"
                            />
                            <span class="text-data text-caption text-medium-emphasis">
                                {{ row.totalTickets - row.openTickets }}/{{ row.totalTickets }}
                            </span>
                        </div>
                        <v-menu>
                            <template #activator="{ props: menuProps }">
                                <!-- The row is a router link, so stop the click from navigating -->
                                <v-btn
                                    v-bind="menuProps"
                                    variant="text"
                                    size="small"
                                    density="comfortable"
                                    icon="mdi-dots-vertical"
                                    class="ml-1"
                                    @click.stop.prevent
                                />
                            </template>
                            <v-list density="compact">
                                <v-list-item
                                    title="Hide project for me"
                                    subtitle="Only affects what you see"
                                    value="hide-for-me"
                                    prepend-icon="mdi-eye-off-outline"
                                    @click="() => hideProjectForMe(row.projectId)"
                                />
                                <v-list-item
                                    v-if="canHide(row.projectId)"
                                    :title="`Hide ${row.versionName} from radar`"
                                    subtitle="For everyone on this instance"
                                    value="hide"
                                    prepend-icon="mdi-earth"
                                    @click="() => hide(row.projectId, row.versionId)"
                                />
                            </v-list>
                        </v-menu>
                    </template>
                </v-list-item>
            </v-list>
            <v-card-actions v-if="hiddenCount > 0" class="px-4 py-2">
                <v-btn
                    variant="text"
                    size="small"
                    color="primary"
                    prepend-icon="mdi-eye-off-outline"
                    @click="hiddenDialog = true"
                >
                    <span class="text-data">{{ hiddenCount }}</span>
                    &nbsp;hidden
                </v-btn>
            </v-card-actions>
        </v-card>

        <v-row>
            <v-col cols="12" md="7">
                <v-card class="mb-4">
                    <v-card-title class="d-flex align-center">
                        <span>My open tickets</span>
                        <v-chip v-if="!loading" size="small" class="ml-2">
                            {{ myTickets.length }}
                        </v-chip>
                        <v-spacer />
                        <v-btn
                            variant="text"
                            size="small"
                            append-icon="mdi-arrow-right"
                            @click="navigateToSearch"
                        >
                            Search
                        </v-btn>
                    </v-card-title>
                    <v-divider />
                    <v-skeleton-loader v-if="loading" type="list-item-two-line@3" />
                    <EmptyState
                        v-else-if="myTickets.length === 0"
                        icon="mdi-check-circle-outline"
                        title="You're all caught up"
                        text="No open tickets are assigned to you right now."
                    />
                    <v-list v-else class="striped py-0">
                        <v-list-item
                            v-for="ticket in myTickets"
                            :key="ticket.id"
                            :to="{ name: 'TicketView', params: { ticketId: ticket.key } }"
                            :title="ticket.title"
                        >
                            <template #subtitle>
                                <TicketKey :ticket-key="ticket.key" :link="false" />
                            </template>
                            <template #append>
                                <v-chip v-if="statusName(ticket)" size="small" variant="tonal">
                                    {{ statusName(ticket) }}
                                </v-chip>
                                <span
                                    v-if="ticket.dueDate"
                                    class="text-data text-caption ml-3"
                                    :class="
                                        isOverdue(ticket.dueDate)
                                            ? 'text-error'
                                            : 'text-medium-emphasis'
                                    "
                                >
                                    due {{ formatMonthDay(ticket.dueDate) }}
                                </span>
                            </template>
                        </v-list-item>
                    </v-list>
                </v-card>

                <HomeActivity />
            </v-col>

            <v-col cols="12" md="5">
                <v-card class="mb-4">
                    <v-card-title>Follow-ups</v-card-title>
                    <v-divider />
                    <v-skeleton-loader v-if="loading" type="list-item@2" />
                    <EmptyState
                        v-else-if="followUps.length === 0"
                        icon="mdi-bell-check-outline"
                        title="Nothing coming due"
                        text="Follow-ups on your tickets will surface here as their dates approach."
                    />
                    <v-list v-else class="striped py-0">
                        <v-list-item
                            v-for="followUp in followUps"
                            :key="followUp.id"
                            :to="{
                                name: 'TicketView',
                                params: { ticketId: followUp.ticketKey },
                            }"
                            :title="followUp.ticketTitle"
                        >
                            <template #subtitle>
                                <TicketKey :ticket-key="followUp.ticketKey" :link="false" />
                            </template>
                            <template #append>
                                <v-chip
                                    size="small"
                                    :color="dueChip(followUp.endDate).color"
                                    :variant="dueChip(followUp.endDate).variant"
                                >
                                    {{ dueChip(followUp.endDate).label }}
                                </v-chip>
                            </template>
                        </v-list-item>
                    </v-list>
                </v-card>

                <v-card>
                    <v-card-title>Workspaces</v-card-title>
                    <v-divider />
                    <v-skeleton-loader v-if="loadingWorkspaces" type="list-item@2" />
                    <EmptyState
                        v-else-if="organizations.length === 0"
                        icon="mdi-domain"
                        title="No organizations yet"
                        text="Organizations you're added to will appear here."
                    />
                    <div v-else class="py-1">
                        <div
                            v-for="organization in organizations"
                            :key="organization.id"
                            class="px-4 py-2 home-workspace"
                        >
                            <div class="text-overline text-medium-emphasis">
                                {{ organization.name }}
                            </div>
                            <div class="d-flex flex-wrap ga-2 mt-1">
                                <v-chip
                                    v-for="project in getProjects(organization.id)"
                                    :key="project.id"
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    @click="navigateToProjectSearch(project.id)"
                                >
                                    {{ project.name }}
                                </v-chip>
                                <span
                                    v-if="getProjects(organization.id).length === 0"
                                    class="text-caption text-medium-emphasis"
                                >
                                    No projects yet
                                </span>
                            </div>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="hiddenDialog" scrollable class="dialog-sm">
            <DialogCard
                title="Hidden from radar"
                context="Release radar"
                @close="hiddenDialog = false"
            >
                <template v-if="hiddenVersions.length > 0">
                    <v-list-subheader>Hidden releases - everyone</v-list-subheader>
                    <v-list class="py-0">
                        <v-list-item
                            v-for="version in hiddenVersions"
                            :key="version.versionId"
                            class="home-hidden-row"
                        >
                            <template #title>
                                <span class="font-weight-medium">{{ version.projectName }}</span>
                                <span class="text-data text-medium-emphasis ml-2">
                                    {{ version.versionName }}
                                </span>
                            </template>
                            <template #append>
                                <v-btn
                                    v-if="canHide(version.projectId)"
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    prepend-icon="mdi-eye-outline"
                                    @click="() => unhide(version.projectId, version.versionId)"
                                >
                                    Unhide
                                </v-btn>
                            </template>
                        </v-list-item>
                    </v-list>
                </template>
                <template v-if="hiddenRadarProjects.length > 0">
                    <v-list-subheader>Hidden projects - only you</v-list-subheader>
                    <v-list class="py-0">
                        <v-list-item
                            v-for="project in hiddenRadarProjects"
                            :key="project.id"
                            class="home-hidden-row"
                        >
                            <template #title>
                                <span class="font-weight-medium">{{ project.name }}</span>
                            </template>
                            <template #append>
                                <v-btn
                                    variant="text"
                                    size="small"
                                    color="primary"
                                    prepend-icon="mdi-eye-outline"
                                    @click="() => unhideProjectForMe(project.id)"
                                >
                                    Unhide
                                </v-btn>
                            </template>
                        </v-list-item>
                    </v-list>
                </template>
                <EmptyState
                    v-if="hiddenCount === 0"
                    icon="mdi-eye-off-outline"
                    title="Nothing hidden"
                    text="Releases and projects you hide from the radar will appear here."
                />
                <template #actions>
                    <v-btn variant="text" color="secondary" @click="hiddenDialog = false">
                        Close
                    </v-btn>
                </template>
            </DialogCard>
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">
import { useAppBarItem } from "@/composables/appBar";
import { OrganizationId } from "@/api/organizations";
import { Project, ProjectId, ProjectVersionId, ProjectPermission } from "@/api/projects";
import { HomeRadarRow, HomeTicket } from "@/api/home";
import { useOrganizationsStore } from "@/store/organizations";
import { usePreferencesStore } from "@/store/preferences";
import { useProjectsStore } from "@/store/projects";
import { homeDataQuery } from "@/queries/home";
import { useQuery } from "@pinia/colada";
import { useTicketLookups } from "@/queries/tickets";
import { ref, onMounted, computed } from "vue";
import { formatMonthDay } from "@/dateTimeUtils";
import { useAuthStore } from "@/store/auth";
import { useRouter } from "vue-router";
import {
    useTicketSearchStore,
    defaultSearchRequest,
    searchRequestToQuery,
} from "@/store/ticketSearch";
import EmptyState from "@/components/common/EmptyState.vue";
import HomeActivity from "@/components/home/HomeActivity.vue";
import TicketKey from "@/components/common/TicketKey.vue";
import VersionChip from "@/components/common/VersionChip.vue";
import DialogCard from "@/components/common/DialogCard.vue";
import { useAlertsStore, AlertPriority } from "@/store/alerts";

const orgStore = useOrganizationsStore();
const prefStore = usePreferencesStore();
const projStore = useProjectsStore();
const homeQuery = useQuery(homeDataQuery);
const ticketLookups = useTicketLookups();
const ticketSearchStore = useTicketSearchStore();
const authStore = useAuthStore();
const alertsStore = useAlertsStore();
const router = useRouter();

const DAY_MS = 24 * 60 * 60 * 1000;

const loadingWorkspaces = ref(false);
const hiddenDialog = ref(false);

const loading = computed(() => homeQuery.isPending.value);
// The server keeps sending every row; the per-user hidden projects only filter the view,
// so unhiding is instant and needs no refetch.
const radar = computed(() =>
    (homeQuery.data.value?.radar ?? []).filter(
        (row) => !prefStore.radarHiddenProjectIds.includes(row.projectId)
    )
);
const hiddenVersions = computed(() => homeQuery.data.value?.hiddenVersions ?? []);
const hiddenRadarProjects = computed(() =>
    prefStore.radarHiddenProjectIds.map((id) => ({
        id,
        name: projStore.get(id)?.name ?? "Unknown project",
    }))
);
const hiddenCount = computed(() => hiddenVersions.value.length + hiddenRadarProjects.value.length);
const myTickets = computed(() => homeQuery.data.value?.myTickets ?? []);
const followUps = computed(() => homeQuery.data.value?.followUps ?? []);

const organizations = computed(() => Array.from(orgStore.organizations ?? [], ([_, org]) => org));

const greeting = computed(() => {
    const name = authStore.user?.displayName ?? authStore.user?.email;
    const hour = new Date().getHours();
    const timeOfDay = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
    return name ? `Good ${timeOfDay}, ${name}` : `Good ${timeOfDay}`;
});

const digest = computed(() => {
    if (homeQuery.data.value === undefined) return null;

    const now = Date.now();
    const horizon = now + 14 * DAY_MS;
    const freezes = radar.value.filter(
        (r) => r.freezeDate && r.freezeDate.getTime() > now && r.freezeDate.getTime() <= horizon
    ).length;
    const releases = radar.value.filter(
        (r) => r.endDate && r.endDate.getTime() > now && r.endDate.getTime() <= horizon
    ).length;

    const parts: string[] = [];
    if (freezes > 0) parts.push(`${freezes} ${freezes === 1 ? "freeze" : "freezes"}`);
    if (releases > 0) parts.push(`${releases} ${releases === 1 ? "release" : "releases"}`);

    const schedule =
        parts.length > 0 ? `${parts.join(" and ")} in the next two weeks` : "a quiet fortnight";
    const tickets =
        myTickets.value.length > 0
            ? `${myTickets.value.length} ${myTickets.value.length === 1 ? "ticket needs" : "tickets need"} you`
            : "nothing assigned to you";

    return `${schedule} · ${tickets}`;
});

function rowVersion(row: HomeRadarRow) {
    return { name: row.versionName, freezeDate: row.freezeDate, releaseDate: row.releaseDate };
}

// The radar row is itself a router link to the project's Versions tab, so these
// version-scoped ticket shortcuts navigate programmatically after stopping the
// row's own navigation rather than nesting anchors inside it.
function viewVersionTickets(projectId: ProjectId, versionId: ProjectVersionId, openOnly = false) {
    const request = { ...defaultSearchRequest(), project: projectId, fixVersions: [versionId] };
    if (openOnly) request.resolved = false;
    void router.push({ name: "TicketSearch", query: searchRequestToQuery(request) });
}

// Dates lead the subtitle so a long theme truncates against the one-line clamp instead of them.
function scheduleFor(row: HomeRadarRow): string {
    const parts: string[] = [];
    if (row.freezeDate) parts.push(`freezes ${formatMonthDay(row.freezeDate)}`);
    parts.push(row.endDate ? `releases ${formatMonthDay(row.endDate)}` : "no release date");
    return parts.join(" · ");
}

type PhaseChip = { label: string; color: string; variant: "flat" | "tonal" };

function phaseFor(row: HomeRadarRow): PhaseChip | null {
    if (!row.freezeDate) return null;

    const untilFreeze = row.freezeDate.getTime() - Date.now();
    if (untilFreeze <= 0) return { label: "Frozen", color: "freeze", variant: "flat" };

    const days = Math.ceil(untilFreeze / DAY_MS);
    if (days <= 7) {
        return {
            label: `Freeze in ${days} ${days === 1 ? "day" : "days"}`,
            color: "freeze",
            variant: "tonal",
        };
    }

    return {
        label: `Freeze ${formatMonthDay(row.freezeDate)}`,
        color: "secondary",
        variant: "tonal",
    };
}

function progressFor(row: HomeRadarRow): number {
    if (row.totalTickets === 0) return 0;
    return ((row.totalTickets - row.openTickets) / row.totalTickets) * 100;
}

function canHide(projectId: ProjectId): boolean {
    return authStore.hasPermission(projectId, ProjectPermission.CanEditVersions);
}

// Both toggle a global version flag, then refetch: hiding promotes the next
// unreleased version into the capped radar, and unhiding pulls back a version
// the radar list never held - neither can be reconstructed locally.
async function hide(projectId: ProjectId, versionId: ProjectVersionId) {
    await projStore.setVersionHiddenFromRadar(projectId, versionId, true);
    await homeQuery.refetch();
}

async function hideProjectForMe(projectId: ProjectId) {
    await prefStore.toggleRadarHiddenProject(projectId);
}

async function unhideProjectForMe(projectId: ProjectId) {
    await prefStore.toggleRadarHiddenProject(projectId);
}

async function unhide(projectId: ProjectId, versionId: ProjectVersionId) {
    const unhidden = hiddenVersions.value.find((v) => v.versionId === versionId);
    await projStore.setVersionHiddenFromRadar(projectId, versionId, false);
    await homeQuery.refetch();

    if (unhidden) {
        alertsStore.enqueueAlert({
            contents: `${unhidden.projectName} ${unhidden.versionName} is back on the radar.`,
            priority: AlertPriority.Info,
        });
    }
}

function statusName(ticket: HomeTicket): string | undefined {
    return ticketLookups.statuses.value?.get(ticket.ticketStatusId)?.name;
}

function isOverdue(date: Date): boolean {
    return date.getTime() < Date.now();
}

type DueChip = { label: string; color: string | undefined; variant: "flat" | "tonal" };

function dueChip(endDate: Date | null): DueChip {
    if (!endDate) return { label: "no date", color: undefined, variant: "tonal" };

    const days = Math.ceil((endDate.getTime() - Date.now()) / DAY_MS);
    if (days < 0) {
        return { label: `${-days}d overdue`, color: "error", variant: "tonal" };
    }
    if (days === 0) {
        return { label: "due today", color: "freeze", variant: "tonal" };
    }
    if (days === 1) {
        return { label: "tomorrow", color: undefined, variant: "tonal" };
    }
    return { label: `in ${days} days`, color: undefined, variant: "tonal" };
}

function getProjects(id: OrganizationId): Project[] {
    return projStore.getOrganizationProjects(id) ?? [];
}

function navigateToProjectSearch(projectId: ProjectId) {
    ticketSearchStore.applyContext({ project: projectId, resolved: false });
    void router.push({ name: "TicketSearch" });
}

function navigateToSearch() {
    ticketSearchStore.applyContext({ assignee: authStore.user?.id ?? null, resolved: false });
    void router.push({ name: "TicketSearch" });
}

useAppBarItem(ref({ key: "Home", title: "Home" }));

onMounted(async () => {
    loadingWorkspaces.value = true;
    try {
        await Promise.all([
            orgStore.loadOrganizations(),
            projStore.loadProjects(),
            prefStore.loadPreferences(),
        ]);
    } finally {
        loadingWorkspaces.value = false;
    }
});
</script>

<style lang="scss">
.home__greeting {
    margin: 0;
    font-size: 1.375rem;
    font-weight: 500;
    line-height: 1.25;
}

.home-radar__progress {
    width: 150px;

    .v-progress-linear {
        min-width: 80px;
    }
}

.home-radar__theme {
    color: rgb(var(--v-theme-primary));
}

.home-radar__version {
    display: inline-flex;
    border-radius: 12px;
    outline-offset: 2px;
    cursor: pointer;

    .version-chip {
        cursor: pointer;
    }

    &:hover .version-chip {
        text-decoration: underline;
    }
}

.home-workspace + .home-workspace {
    border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
